import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next/pages';
import DownArrowIcon from 'assets/svgs/DownArrowIcon';
import { SolidButton } from 'components/Button';
import {
  PackageDestinationNode,
  PackageHotelName,
} from 'api/queries/packageTour';
import { Stars } from 'components/PackageTours/HotelCard';
import { flattenDestinations, offeredHotels } from 'utils/packageSearch';
import { Destination } from './types';
import FieldTrigger from './FieldTrigger';
import { arrivalIcon } from 'assets/svgs/brandIcons';

interface IDestinationDropdownProps {
  value: string;
  // packageSearchReferenceData destinations: country → region → resort.
  destinations: PackageDestinationNode[];
  // WHIT-501: a translation key said instead of the lists while there are
  // no destinations: loading, or the search data could not be loaded.
  notice?: string | null;
  // WHIT-503: hotels whose name matches what is typed (operation 15), the
  // lookup still running, or a translation key saying it failed.
  hotels?: PackageHotelName[];
  hotelsLoading?: boolean;
  hotelsNotice?: string | null;
  selected: Destination | null;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onChange: (text: string) => void;
  onSelect: (dest: Destination | null) => void;
}

const destinationLabel = (d: Destination) =>
  d.breadcrumb ? `${d.name} (${d.breadcrumb})` : d.name;

const DestinationDropdown: React.FC<IDestinationDropdownProps> = ({
  value,
  destinations,
  notice,
  hotels = [],
  hotelsLoading = false,
  hotelsNotice = null,
  selected,
  isOpen,
  onToggle,
  onClose,
  onChange,
  onSelect,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [panelTop, setPanelTop] = useState(0);

  const allDestinations: Destination[] = flattenDestinations(destinations);

  const filteredDestinations =
    value.length >= 2
      ? allDestinations
          .filter(
            d =>
              d.name.toLowerCase().includes(value.toLowerCase()) ||
              (d.breadcrumb &&
                d.breadcrumb.toLowerCase().includes(value.toLowerCase())),
          )
          .slice(0, 20)
      : [];

  const isFiltering = value.length >= 2;
  const hotelChoices: Destination[] = isFiltering ? offeredHotels(hotels) : [];
  // Matches first, then the hotels; Confirm takes the first of them.
  const choices = [...filteredDestinations, ...hotelChoices];
  const showHotels = hotelChoices.length > 0 || hotelsLoading || !!hotelsNotice;

  useEffect(() => {
    if (isOpen) {
      // The field's wrapper, not the button: FieldTrigger puts the button
      // in a positioned wrapper of its own, so the button's offsetTop is 0
      // and a phone's panel opened at the top of the form (WHIT-497).
      if (fieldRef.current) {
        const field = fieldRef.current;
        setPanelTop(field.offsetTop + field.offsetHeight + 4);
      }
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSelectedCountry(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selected && choices.length > 0) {
      const dest = choices[0];
      onSelect(dest);
      onChange(destinationLabel(dest));
    }
    onClose();
  };

  const triggerLabel = selected ? destinationLabel(selected) : null;

  const selectedCountryData = selectedCountry
    ? destinations.find(c => c.id === selectedCountry)
    : null;

  // The country's regions and resorts, in tree order.
  const regionsForCountry: Destination[] = selectedCountryData
    ? flattenDestinations(selectedCountryData.children, [
        selectedCountryData.name,
      ])
    : [];

  return (
    <div ref={fieldRef} className="flex flex-col gap-1 flex-1 min-w-0">
      <FieldTrigger
        ref={triggerRef}
        label={t('Where to?')}
        icon={arrivalIcon}
        open={isOpen}
        filled={!!(selected || value)}
        onClick={onToggle}
        onClear={
          selected || value
            ? () => {
                // The clear button disappears with the value; keyboard focus
                // goes back to the field instead of leaving the search.
                triggerRef.current?.focus();
                onChange('');
                onSelect(null);
                if (isOpen) onClose();
              }
            : undefined
        }
        clearLabel={t('Clear selection')}
      >
        <span className={triggerLabel ? 'text-black' : 'text-gray-400'}>
          {triggerLabel || t('Destination / accommodation search')}
        </span>
      </FieldTrigger>

      {isOpen && (
        <div
          className="pkg-destination-panel absolute left-0 right-0 z-50 bg-white rounded-xl shadow-xl border border-tt-gray-purple"
          style={{ top: panelTop }}
        >
          <div className="p-4 border-b border-tt-gray-purple">
            <input
              ref={inputRef}
              type="text"
              placeholder={t('Filter...') || ''}
              value={value}
              onChange={e => {
                onChange(e.target.value);
                onSelect(null);
              }}
              className="w-full h-10 rounded border border-tt-gray-purple px-3 text-base focus:outline-hidden focus:ring-2 focus:ring-tt-dark-purple"
            />
          </div>

          {isFiltering ? (
            <div className="p-2 max-h-64 overflow-y-auto">
              {notice ? (
                // The places are not loaded; the hotels may still be found.
                <p
                  role="status"
                  className="text-sm text-gray-500 p-4 text-center"
                >
                  {t(notice)}
                </p>
              ) : choices.length === 0 && !showHotels ? (
                <p className="text-sm text-gray-400 p-4 text-center">
                  {t('No results found')}
                </p>
              ) : (
                filteredDestinations.map((dest, i) => (
                  <button
                    key={`${dest.level}-${dest.id}-${i}`}
                    type="button"
                    onClick={() => {
                      onSelect(dest);
                      onChange(destinationLabel(dest));
                      onClose();
                    }}
                    className="w-full text-left px-4 py-2.5 hover:bg-tt-verylight-purple text-base transition-colors border-b border-tt-gray-purple last:border-0"
                  >
                    <span className="font-medium">{dest.name}</span>
                    {dest.breadcrumb && (
                      <span className="text-sm text-gray-500 ml-1">
                        ({dest.breadcrumb})
                      </span>
                    )}
                  </button>
                ))
              )}
              {showHotels && (
                <>
                  <p className="px-4 pt-3 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-tt-gray-purple">
                    {t('Accommodation')}
                  </p>
                  {hotelChoices.map(hotel => (
                    <button
                      key={`hotel-${hotel.id}`}
                      type="button"
                      onClick={() => {
                        onSelect(hotel);
                        onChange(hotel.name);
                        onClose();
                      }}
                      className="w-full text-left px-4 py-2.5 hover:bg-tt-verylight-purple text-base transition-colors border-b border-tt-gray-purple last:border-0"
                    >
                      <span className="font-medium">{hotel.name}</span>
                      {!!hotel.category && (
                        <span className="ml-1">
                          <Stars category={hotel.category} />
                        </span>
                      )}
                      {hotel.breadcrumb && (
                        <span className="block text-sm text-gray-500">
                          {hotel.breadcrumb}
                        </span>
                      )}
                    </button>
                  ))}
                  {hotelChoices.length === 0 && (
                    <p
                      role="status"
                      className="text-sm text-gray-500 p-4 text-center"
                    >
                      {t(hotelsNotice || 'Loading...')}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : notice ? (
            <p role="status" className="text-sm text-gray-500 p-6 text-center">
              {t(notice)}
            </p>
          ) : (
            <div className="flex min-h-[280px] max-h-[420px]">
              {/* Left: country list */}
              <div className="w-1/2 border-r border-tt-gray-purple overflow-y-auto">
                <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-tt-gray-purple">
                  {t('Country')}
                </p>
                {destinations.map(country => (
                  <button
                    key={country.id}
                    type="button"
                    onClick={() => {
                      if (selectedCountry === country.id) {
                        // Double-click selects the country itself
                        onSelect({
                          id: country.id,
                          level: country.level,
                          name: country.name,
                        });
                        onChange(country.name);
                        onClose();
                      } else {
                        setSelectedCountry(country.id);
                      }
                    }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                      selectedCountry === country.id
                        ? 'bg-tt-verylight-purple text-tt-dark-purple font-semibold'
                        : 'hover:bg-tt-verylight-purple'
                    }`}
                  >
                    <span>{country.name}</span>
                    {!!country.children?.length && (
                      <DownArrowIcon
                        className="-rotate-90 h-3 w-3 shrink-0"
                        color={
                          selectedCountry === country.id
                            ? 'var(--tt-dark-purple)'
                            : 'var(--tt-light-purple)'
                        }
                      />
                    )}
                  </button>
                ))}
              </div>
              {/* Right: regions */}
              <div className="w-1/2 overflow-y-auto">
                <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-tt-gray-purple">
                  {t('Region')}
                </p>
                {regionsForCountry.length === 0 ? (
                  <p className="text-sm text-gray-400 p-4">
                    {selectedCountry
                      ? t('No regions available')
                      : t('Select a country')}
                  </p>
                ) : (
                  regionsForCountry.map((r, i) => (
                    <button
                      key={`${r.level}-${r.id}-${i}`}
                      type="button"
                      onClick={() => {
                        onSelect(r);
                        onChange(r.name);
                        onClose();
                      }}
                      className={`w-full text-left py-2.5 pr-4 text-sm hover:bg-tt-verylight-purple transition-colors text-tt-dark-purple border-b border-tt-gray-purple last:border-0 ${
                        r.level === 'resort' ? 'pl-8' : 'pl-4'
                      }`}
                    >
                      {r.name}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="p-3 border-t border-tt-gray-purple flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-tt-dark-purple hover:text-tt-verydark-purple font-medium"
            >
              {t('Cancel')}
            </button>
            <SolidButton onClick={handleConfirm} className="!py-2.5 !px-6">
              {t('Confirm')}
            </SolidButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default DestinationDropdown;
